import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarDays,
  CircleCheck,
  ClipboardList,
  Clock,
  FilePlus2,
  FileText,
  FolderPlus,
  Mail,
  Pill,
  Plus,
  ClipboardPaste,
  Scale,
  Stethoscope,
  Check,
} from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import type { Accueil as DonneesAccueil, PatientResume } from '../lib/types'
import { Carte, Champ, Chargement, EtatVide, Message, Modale, Pastille } from '../composants/base'
import SelecteurPatient from '../composants/SelecteurPatient'
import AjoutDocument from '../composants/AjoutDocument'
import * as f from '../lib/format'
import { CATEGORIES_DONNEE, TYPES_VENUE } from '@doc/shared'

export default function Accueil() {
  const naviguer = useNavigate()
  const client = useQueryClient()
  const [selecteur, setSelecteur] = useState<null | 'venue'>(null)
  const [ajout, setAjout] = useState<null | 'fichier' | 'texte'>(null)
  const [alerteATraiter, setAlerteATraiter] = useState<{ id: string; libelle: string } | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['accueil'],
    queryFn: () => api<DonneesAccueil>('/api/accueil'),
  })

  const nouveauPatient = useMutation({
    mutationFn: () => api<{ patient: PatientResume }>('/api/patients', { methode: 'POST', corps: {} }),
    onSuccess: ({ patient }) => {
      client.invalidateQueries({ queryKey: ['accueil'] })
      naviguer(`/patients/${patient.id}?identite=1`)
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const tache = useMutation({
    mutationFn: (params: { id: string; faite: boolean }) =>
      api(`/api/taches/${params.id}`, { methode: 'PATCH', corps: { faite: params.faite } }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['accueil'] }),
  })

  if (isLoading || !data) return <Chargement />

  const aVerifier = data.propositions.length
  const conflits = data.conflits.length

  return (
    <div className="mx-auto max-w-[1180px] pt-1">
      <h1 className="mb-4 text-[20px] font-semibold tracking-tight">Accueil</h1>

      {erreur && (
        <div className="mb-4">
          <Message ton="critique">{erreur}</Message>
        </div>
      )}

      {/* Actions rapides (cahier des charges, section 18) */}
      <div className="mb-5 flex flex-wrap gap-2">
        <button
          className="bouton-primaire"
          onClick={() => nouveauPatient.mutate()}
          disabled={nouveauPatient.isPending}
        >
          <FolderPlus className="h-4 w-4" />
          Nouveau patient
        </button>
        <button className="bouton-doux" onClick={() => setSelecteur('venue')}>
          <CalendarDays className="h-4 w-4" />
          Nouvelle venue
        </button>
        <button className="bouton-doux" onClick={() => setAjout('fichier')}>
          <FilePlus2 className="h-4 w-4" />
          Ajouter un document
        </button>
        <button className="bouton-doux" onClick={() => setAjout('texte')}>
          <ClipboardPaste className="h-4 w-4" />
          Coller un texte
        </button>
        <button
          className="bouton-discret cursor-not-allowed opacity-60"
          title="Les ordonnances arrivent en phase 4"
          disabled
        >
          <Pill className="h-4 w-4" />
          Créer une ordonnance
          <span className="text-[10px] text-brume">à venir</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.6fr_1fr]">
        <div className="flex flex-col gap-5">
          {/* 1. Consultations et venues du jour */}
          <Carte
            titre="Venues du jour"
            icone={<CalendarDays className="h-[17px] w-[17px] text-bleu-500" />}
            compte={data.venuesDuJour.length}
          >
            {data.venuesDuJour.length === 0 ? (
              <EtatVide texte="Aucune venue prévue aujourd’hui." />
            ) : (
              <ul className="px-2 pb-2">
                {data.venuesDuJour.map((v) => (
                  <li key={v.id}>
                    <Link
                      to={`/patients/${v.patient.id}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-bleu-50"
                    >
                      {/* Seule la date d'une venue est saisie : afficher une heure
                          reviendrait à en inventer une. */}
                      <Pastille ton="bleu">
                        {TYPES_VENUE[v.type as keyof typeof TYPES_VENUE]}
                      </Pastille>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-medium">
                          {v.patient.affichage}
                        </span>
                        <span className="block truncate text-[12.5px] text-brume">
                          {v.motif ?? 'Motif non renseigné'}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Carte>

          {/* 3. Conflits, mis en avant avant tout le reste */}
          {conflits > 0 && (
            <Carte
              titre="Désaccords à trancher"
              icone={<Scale className="h-[17px] w-[17px] text-critique" />}
              compte={conflits}
            >
              <div className="flex flex-col gap-2 px-5 pb-4">
                {data.conflits.map((c) => (
                  <Link
                    key={c.id}
                    to={`/patients/${c.patient?.id}?conflit=${c.id}`}
                    className="rounded-lg bg-critique-fond px-3.5 py-3 hover:brightness-[0.98]"
                  >
                    <div className="flex items-center gap-2 text-[13px] font-medium text-critique">
                      <AlertTriangle className="h-4 w-4" />
                      {c.patient?.affichage}
                    </div>
                    <div className="mt-1 text-[13px] text-encre">
                      « {c.existante.libelle} » contre « {c.proposee.libelle} »
                    </div>
                    {c.explication && (
                      <div className="mt-0.5 text-[12px] text-ardoise">{c.explication}</div>
                    )}
                  </Link>
                ))}
              </div>
            </Carte>
          )}

          {/* 3. Documents analysés par l'IA à vérifier */}
          <Carte
            titre="À vérifier avant d’entrer au dossier"
            icone={<ClipboardList className="h-[17px] w-[17px] text-attention" />}
            compte={aVerifier}
          >
            {aVerifier === 0 ? (
              <EtatVide texte="Rien en attente de validation. L’analyse automatique des documents arrive en phase 2 : les informations proposées apparaîtront ici avant d’entrer dans un dossier." />
            ) : (
              <ul className="px-2 pb-2">
                {data.propositions.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={
                        p.analyseId
                          ? `/verification/${p.analyseId}`
                          : `/patients/${p.patient.id}`
                      }
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-bleu-50"
                    >
                      <Stethoscope className="h-4 w-4 shrink-0 text-attention" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px]">{p.libelle}</span>
                        <span className="block truncate text-[12.5px] text-brume">
                          {CATEGORIES_DONNEE[p.categorie]} · {p.patient.affichage}
                        </span>
                      </span>
                      <Pastille ton="attention">À vérifier</Pastille>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Carte>

          {/* 2. Alertes biologiques non traitées */}
          <Carte
            titre="Alertes biologiques"
            icone={<AlertTriangle className="h-[17px] w-[17px] text-attention" />}
            compte={data.alertes.length}
          >
            {data.alertes.length === 0 ? (
              <EtatVide texte="Aucune alerte à traiter. Les seuils personnels et les alertes biologiques arrivent en phase 3." />
            ) : (
              <ul className="px-2 pb-2">
                {data.alertes.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-bleu-50">
                    <Link to={`/patients/${a.patient?.id}`} className="min-w-0 flex-1">
                      <span className="block truncate text-[14px]">{a.libelle}</span>
                      <span className="block truncate text-[12.5px] text-brume">
                        {a.patient?.affichage} · {f.ilYA(a.creeLe)}
                      </span>
                    </Link>
                    <Pastille ton={a.severite === 'CRITIQUE' ? 'critique' : 'attention'}>
                      {a.severite === 'CRITIQUE' ? 'Critique' : 'Attention'}
                    </Pastille>
                    <button
                      className="bouton-discret shrink-0 px-2"
                      onClick={() => setAlerteATraiter({ id: a.id, libelle: a.libelle })}
                      title="Marquer comme traitée"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Carte>
        </div>

        <div className="flex flex-col gap-5">
          {/* 4. Mails non traités */}
          <Carte titre="Messagerie" icone={<Mail className="h-[17px] w-[17px] text-bleu-500" />}>
            <div className="px-5 pb-4">
              <Message ton="info">
                La messagerie n’est pas raccordée (phase 5). Aucune boîte n’est relevée : ce qui
                s’affiche ici ne veut pas dire qu’il n’y a pas de message.
              </Message>
            </div>
          </Carte>

          {/* Tâches personnelles */}
          <Carte
            titre="Mes tâches"
            icone={<CircleCheck className="h-[17px] w-[17px] text-bleu-500" />}
            compte={data.taches.length}
          >
            {data.taches.length === 0 ? (
              <EtatVide texte="Aucune tâche en cours." />
            ) : (
              <ul className="px-3 pb-3">
                {data.taches.map((t) => (
                  <li key={t.id} className="flex items-start gap-2.5 px-2 py-2">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-[var(--color-bleu-600)]"
                      checked={t.faite}
                      onChange={(e) => tache.mutate({ id: t.id, faite: e.target.checked })}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13.5px]">{t.libelle}</span>
                      <span className="block text-[12px] text-brume">
                        {t.patient ? `${t.patient.affichage}` : 'Sans dossier'}
                        {t.echeance ? ` · pour le ${f.dateCourte(t.echeance)}` : ''}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="px-5 pb-4">
              <NouvelleTache />
            </div>
          </Carte>

          {/* Patients récemment consultés ou modifiés */}
          <Carte
            titre="Dossiers récents"
            icone={<Clock className="h-[17px] w-[17px] text-bleu-500" />}
          >
            {data.patientsRecents.length === 0 ? (
              <EtatVide texte="Aucun dossier pour l’instant." />
            ) : (
              <ul className="px-2 pb-2">
                {data.patientsRecents.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/patients/${p.id}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-bleu-50"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px]">{p.affichage}</span>
                        <span className="block truncate text-[12px] text-brume">
                          {p.reference} · modifié {f.ilYA(p.majLe)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Carte>

          {/* Documents récents */}
          <Carte
            titre="Documents récents"
            icone={<FileText className="h-[17px] w-[17px] text-bleu-500" />}
          >
            {data.documentsRecents.length === 0 ? (
              <EtatVide texte="Aucun document déposé." />
            ) : (
              <ul className="px-2 pb-2">
                {data.documentsRecents.map((d) => (
                  <li key={d.id}>
                    <Link
                      to={`/patients/${d.patient?.id}?document=${d.id}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-bleu-50"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px]">{d.titre}</span>
                        <span className="block truncate text-[12px] text-brume">
                          {d.patient?.affichage} · {f.ilYA(d.dateAjout)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Carte>
        </div>
      </div>

      {selecteur === 'venue' && (
        <SelecteurPatient
          titre="Dans quel dossier créer la venue ?"
          fermer={() => setSelecteur(null)}
          choisir={(patient) => naviguer(`/patients/${patient.id}?action=venue`)}
        />
      )}

      {ajout && <AjoutDocument quoi={ajout} fermer={() => setAjout(null)} />}

      {alerteATraiter && (
        <TraiterAlerte alerte={alerteATraiter} fermer={() => setAlerteATraiter(null)} />
      )}
    </div>
  )
}

/** Marquer une alerte comme traitée garde la trace de ce qui a été fait. */
function TraiterAlerte({
  alerte,
  fermer,
}: {
  alerte: { id: string; libelle: string }
  fermer: () => void
}) {
  const client = useQueryClient()
  const [note, setNote] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  const traiter = useMutation({
    mutationFn: () =>
      api(`/api/alertes/${alerte.id}/traiter`, {
        methode: 'POST',
        corps: { note: note || undefined },
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['accueil'] })
      client.invalidateQueries({ queryKey: ['biologie'] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Modale
      titre="Marquer l’alerte comme traitée"
      sousTitre={alerte.libelle}
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => traiter.mutate()}
            disabled={traiter.isPending}
          >
            Marquer comme traitée
          </button>
        </>
      }
    >
      <Champ
        etiquette="Ce qui a été fait (facultatif)"
        aide="Conservé avec ton nom et la date. L’alerte reste consultable."
      >
        <input
          className="champ"
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Patiente rappelée, contrôle prescrit à trois semaines"
        />
      </Champ>
      {erreur && (
        <div className="mt-3">
          <Message ton="critique">{erreur}</Message>
        </div>
      )}
    </Modale>
  )
}

function NouvelleTache() {
  const client = useQueryClient()
  const [libelle, setLibelle] = useState('')
  const ajouter = useMutation({
    mutationFn: () => api('/api/taches', { methode: 'POST', corps: { libelle } }),
    onSuccess: () => {
      setLibelle('')
      client.invalidateQueries({ queryKey: ['accueil'] })
    },
  })

  return (
    <form
      className="flex gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (libelle.trim()) ajouter.mutate()
      }}
    >
      <input
        className="champ"
        placeholder="Rappeler un patient, contrôler un résultat…"
        value={libelle}
        onChange={(e) => setLibelle(e.target.value)}
      />
      <button className="bouton-doux shrink-0" disabled={!libelle.trim() || ajouter.isPending}>
        <Plus className="h-4 w-4" />
      </button>
    </form>
  )
}
